import { format } from 'date-fns'

const BASE = '/.netlify/functions/tmb'

// ── Planner API ───────────────────────────────────────────────────────────────

export async function planRoute(fromLat, fromLng, toLat, toLng, options = {}) {
  const {
    date = new Date(),
    time = '09:00am',
    mode = 'TRANSIT,WALK',
  } = options

  const dateStr = format(date instanceof Date ? date : new Date(date), 'MM/dd/yyyy')

  const params = new URLSearchParams({
    fromPlace:      `${fromLat},${fromLng}`,
    toPlace:        `${toLat},${toLng}`,
    date:           dateStr,
    time,
    mode,
    numItineraries: '3',
    locale:         'ca',
  })

  const res = await fetch(`${BASE}/plan?${params}`)
  if (!res.ok) throw new Error(`Planner TMB error ${res.status}`)
  const data = await res.json()
  return data?.plan?.itineraries ?? []
}

export function getBestItinerary(itineraries) {
  if (!itineraries?.length) return null
  return itineraries.reduce((best, cur) =>
    cur.duration < best.duration ? cur : best
  )
}

export function parseItinerary(itinerary) {
  return {
    duracio_total:    Math.round(itinerary.duration / 60),
    temps_caminant:   Math.round(itinerary.walkTime / 60),
    temps_transport:  Math.round(itinerary.transitTime / 60),
    temps_espera:     Math.round(itinerary.waitingTime / 60),
    transbords:       itinerary.transfers,
    segments: itinerary.legs.map(leg => ({
      tipus:    leg.mode,
      origen: {
        nom:    leg.from.name,
        lat:    leg.from.lat,
        lng:    leg.from.lon,
        hora:   new Date(leg.startTime).toLocaleTimeString('ca', { hour: '2-digit', minute: '2-digit' }),
        stopId: leg.from.stopId ?? null,
      },
      desti: {
        nom:    leg.to.name,
        lat:    leg.to.lat,
        lng:    leg.to.lon,
        hora:   new Date(leg.endTime).toLocaleTimeString('ca', { hour: '2-digit', minute: '2-digit' }),
        stopId: leg.to.stopId ?? null,
      },
      duracio:   Math.round((leg.endTime - leg.startTime) / 60000),
      distancia: Math.round(leg.distance),
      linia:     leg.route         ?? null,
      color:     leg.routeColor    ? `#${leg.routeColor}` : null,
      agencia:   leg.agencyName    ?? null,
      direccio:  leg.headsign      ?? null,
    })),
  }
}

// ── Temps real ────────────────────────────────────────────────────────────────

export async function enrichWithRealTime(parsedItinerary) {
  const enrichedSegments = await Promise.all(
    parsedItinerary.segments.map(async (segment) => {
      if (segment.tipus === 'WALK') return segment

      try {
        const stopId = segment.origen?.stopId
        if (!stopId) return segment
        // Format stopId TMB: "3:1.325" → codi = "325"
        const codiParada = String(stopId).split('.').pop()

        if (segment.tipus === 'BUS') {
          const arrivals = await _getBusArrivals(codiParada)
          const nextBus  = arrivals.find(a => a.linia === segment.linia)
          return {
            ...segment,
            temps_espera_real:     nextBus?.minuts ?? null,
            temps_real_disponible: !!nextBus,
          }
        }

        if (segment.tipus === 'SUBWAY') {
          const metroInfo = await _getMetroArrivals(codiParada)
          return {
            ...segment,
            temps_espera_real:     metroInfo?.minuts ?? null,
            temps_real_disponible: !!metroInfo,
          }
        }
      } catch (err) {
        console.warn('[TMB] Error enriquint temps real:', err.message)
      }

      return segment
    })
  )

  return { ...parsedItinerary, segments: enrichedSegments }
}

async function _getBusArrivals(codiParada) {
  const res  = await fetch(`${BASE}/stops/${codiParada}/arrivals`)
  const data = await res.json()
  return (data?.data?.ibus ?? []).map(bus => ({
    linia:      bus.line,
    destinacio: bus.destination,
    minuts:     bus['t-in-min'],
  }))
}

async function _getMetroArrivals(codiEstacio) {
  const res  = await fetch(`${BASE}/metro/estacions?estacions=${codiEstacio}`)
  const data = await res.json()
  return data?.data ?? null
}
